import { faBus, faCar } from '@fortawesome/pro-regular-svg-icons';
import { type BusPathData } from './types';
import BusPriceLoader from './BusPriceLoader';
import { type UrlStringParams } from '@search/types';
import { TripSearchForm } from '@search/trip/TripSearchForm';
import {
    type BusLocation,
    type BusUrlParams,
    type Province,
    provincesToLocations,
} from '@utils/bus';
import {
    ReserveType,
    type VerifyPathError,
    ViewNotFoundError,
    ViewRedirectError,
} from '@utils/itinerary';
import { PaxType, type ReserveInfo, SearchMode } from '@utils/types';
import { TripType } from '@utils/trip';
import {
    BusCitiesApiGetBusCityInfoGet,
    CityListApiApiCityListGetBusCityListGet,
    type CityListApiApiCityListGetBusCityListGetRequest,
    CityListApiApiCityListGetTaxiCityListGet,
    GetNearbyRoutesApiApiGetNearbyRoutesPost,
} from '@clients/BusApi';
import type {
    TripShareInfo,
    TripShareLocations,
} from '@itinerary/trip/TripModuleTypes';

export class BusSearchForm extends TripSearchForm<BusLocation, BusPathData> {
    constructor(
        public specificProviderId: number | null,
        public reserveType:
            | ReserveType.Bus
            | ReserveType.Taxi = ReserveType.Bus,
        maxSeatCount = 20
    ) {
        super();
        this.maxSeatCount = maxSeatCount;
    }

    override get searchHistoryData() {
        if (this.isBus) {
            return super.searchHistoryData;
        }
        return null;
    }

    override async loadStaticLocations() {
        if (!this.originProvinces.length) {
            this.isLoadingLocations = true;
            if (this.reserveType === ReserveType.Bus) {
                this.originProvinces = (
                    await import('~/lib/static/bus_stations.json')
                ).default;
            } else {
                this.originProvinces = (
                    await import('~/lib/static/taxi_stations.json')
                ).default;
            }
            this.isLoadingLocations = false;
        }
    }

    maxSeatCount = 20;

    override popularDomesticOrigins: BusLocation[] = [
        {
            code: 'tehran',
            terminalId: 11320000,
            title: 'تهران',
        },
        {
            code: 'esfahan',
            terminalId: 21310000,
            title: 'اصفهان',
        },
        {
            code: 'mashhad',
            terminalId: 31310000,
            title: 'مشهد',
        },
        {
            code: 'shiraz',
            terminalId: 41310000,
            title: 'شیراز',
        },

        {
            code: 'zahedan',
            terminalId: 61310000,
            title: 'زاهدان',
        },
    ];

    findLocationBaseOnCode(
        code: string,
        locations: BusLocation[]
    ): BusLocation | null {
        return locations.reduce(
            (prevL, l) => {
                if (l.code === code) {
                    return l;
                }
                if (prevL) {
                    return prevL;
                }
                return l.subLocations?.find(s => s.code === code) ?? null;
            },
            null as BusLocation | null
        );
    }

    findLocationBaseOnTerminalId(
        id: number,
        locations: BusLocation[]
    ): BusLocation | null {
        return locations.reduce(
            (prevL, l) => {
                if (l.terminalId === id) {
                    return l;
                }
                if (prevL) {
                    return prevL;
                }
                return l.subLocations?.find(s => s.terminalId === id) ?? null;
            },
            null as BusLocation | null
        );
    }

    override migrateViewPath(pathData: BusPathData) {
        const { origin, destination, departureDate, returnDate, adultCount } =
            pathData;
        const { fullPath, queries } = this.generateViewUrl({
            origin,
            destination,
            departureDate,
            returnDate,
            adultCount,
        });
        // we want to redirect, so no need to double verify with super
        return Promise.reject(
            new ViewRedirectError('Path not valid', fullPath, queries)
        );
    }

    override async verifyViewPath(
        path: string,
        pathData: BusPathData
    ): Promise<void> {
        let { origin, destination, adultCount, departureDate, returnDate } =
            pathData;
        const isNumber = (val: string) => /^\d+$/.test(val);
        const hasNumerals = isNumber(origin) || isNumber(destination);
        if (!hasNumerals) {
            return super.verifyViewPath(path, pathData);
        }
        try {
            await super.verifyViewPath(path, pathData);
            // fixing dates and not same origin/destination
        } catch (e) {
            const err = e as VerifyPathError;
            if (!err.needRedirect) {
                throw err;
            }
            ({ departureDate, returnDate } = err.queries);
        }
        await this.searchLocations(origin, {
            target: 'origin',
            mode: SearchMode.Domestic,
        });
        let originCode = origin;
        let destinationCode = destination;
        if (isNumber(origin)) {
            const terminalId = Number(origin);
            let originLocation: BusLocation | null =
                this.findLocationBaseOnTerminalId(
                    terminalId,
                    this.domesticOrigins
                );
            if (!originLocation) {
                originLocation = this.findLocationBaseOnTerminalId(
                    terminalId,
                    this.foreignOrigins
                );
                this.searchMode = SearchMode.Foreign;
            }
            if (!originLocation || isNumber(originLocation.code)) {
                throw new ViewNotFoundError('origin not found');
            }
            this.setOrigin(originLocation);
            originCode = originLocation.code;
        } else {
            await this.setOriginBaseOnCode(originCode);
            if (!this.originCode) {
                this.searchMode = SearchMode.Foreign;
                await this.setOriginBaseOnCode(originCode);
            }
        }
        await this.loadDestinations(destination);
        if (isNumber(destination)) {
            const terminalId = Number(destination);
            let destinationLocation: BusLocation | null =
                this.findLocationBaseOnTerminalId(
                    terminalId,
                    this.domesticDestinations
                );

            if (!destinationLocation) {
                destinationLocation = this.findLocationBaseOnTerminalId(
                    terminalId,
                    this.foreignDestinations
                );
                this.searchMode = SearchMode.Foreign;
            }
            if (!destinationLocation || isNumber(destinationLocation.code)) {
                throw new ViewNotFoundError('destination not found');
            }
            destinationCode = destinationLocation.code;
        }
        const { fullPath, queries } = this.generateViewUrl({
            origin: originCode,
            destination: destinationCode,
            departureDate,
            returnDate,
            adultCount,
        });
        // we want to redirect, so no need to double verify with super
        return Promise.reject(
            new ViewRedirectError('Path not valid', fullPath, queries)
        );
    }

    private get isBus() {
        return this.reserveType === ReserveType.Bus;
    }

    override async parsePath(pathData: BusPathData): Promise<BusUrlParams> {
        const { origin, destination } = pathData;
        await this.searchLocations(origin, {
            target: 'origin',
            mode: SearchMode.Domestic,
        });
        await this.searchLocations(destination, {
            target: 'destination',
            mode: SearchMode.Domestic,
        });
        let originLocation: BusLocation | null = this.findLocationBaseOnCode(
            origin,
            this.domesticOrigins
        );
        if (!originLocation) {
            originLocation = this.findLocationBaseOnCode(
                origin,
                this.foreignOrigins
            );

            this.searchMode = SearchMode.Foreign;
        }
        if (!originLocation) {
            try {
                const request: { busCityId?: number; code?: string } = {};
                if (/^-?\d+$/.test(origin)) {
                    request.busCityId = +origin;
                } else {
                    request.code = origin;
                }
                const { data } = await BusCitiesApiGetBusCityInfoGet(request);
                originLocation = {
                    code: data.code ?? '',
                    terminalId: data.id ?? 0,
                    title: data.title ?? '',
                    subLocations: data.terminals?.map(terminal => ({
                        code: terminal.code ?? '',
                        terminalId: terminal.id ?? 0,
                        title: terminal.title ?? '',
                    })),
                };
                this.originProvinces.push({
                    provinceName: data.persianTitle ?? '',
                    cities: [
                        {
                            id: data.id!,
                            title: data.title ?? '',
                            isDomestic: data.isDomestic ?? true,
                            isForeign: data.isForeign ?? false,
                            terminals: [],
                            englishTitle: data.englishTitle ?? '',
                            code: data.code,
                        },
                    ],
                });
                this.searchMode = data.isForeign
                    ? SearchMode.Foreign
                    : SearchMode.Domestic;
            } catch (error: any) {
                throw new ViewNotFoundError(
                    'error',
                    error.response?.status ?? 500
                );
            }
        }

        if (!originLocation) {
            throw new ViewNotFoundError('origin not found');
        }

        this.setOrigin(originLocation);
        await this.loadDestinations(destination);
        let destinationLocation: BusLocation | null =
            this.findLocationBaseOnCode(destination, this.domesticDestinations);

        if (!destinationLocation) {
            destinationLocation = this.findLocationBaseOnCode(
                destination,
                this.foreignDestinations
            );
            this.searchMode = SearchMode.Foreign;
        }
        if (!destinationLocation) {
            throw new ViewNotFoundError('destination not found');
        }
        this.setDestination(destinationLocation);
        return {
            origin,
            destination,
            originTerminalId: originLocation.terminalId,
            destinationTerminalId: destinationLocation.terminalId,
            departureDate:
                (pathData.departureDate && parseDate(pathData.departureDate)) ||
                this.defaultDepartureDate,
            adultCount: pathData?.adultCount
                ? parseInt(pathData.adultCount)
                : 1,
            childCount: 0,
            infantCount: 0,
            returnDate:
                (pathData.returnDate && parseDate(pathData.returnDate)) ||
                undefined,
        };
    }

    lastOriginCodeThatDestinationsAreLoadedFor: string | null = null;

    loadDestinationsJob: Promise<void> | null = null;

    async loadDestinations(includeCity?: number | string) {
        if (
            !this.originCode ||
            this.lastOriginCodeThatDestinationsAreLoadedFor === this.originCode
        ) {
            if (this.loadDestinationsJob) {
                await this.loadDestinationsJob;
            }
            return Promise.resolve();
        }
        this.lastOriginCodeThatDestinationsAreLoadedFor = this.originCode;
        const loader = async () => {
            try {
                this.isLoadingLocations = true;
                this.destinationProvinces = [];
                this.setDestination(null);
                const request: CityListApiApiCityListGetBusCityListGetRequest =
                    {
                        fromCity: this.origin?.terminalId,
                        getCities: true,
                        includePopular: this.isDomestic,
                        includeCity: this.destination
                            ? this.destination.terminalId.toString()
                            : includeCity?.toString(),
                        groupTerminals: true,
                    };
                const { data } = await (this.reserveType === ReserveType.Bus
                    ? CityListApiApiCityListGetBusCityListGet(request)
                    : CityListApiApiCityListGetTaxiCityListGet(request));
                if (this.isDomestic) {
                    if (data[0]?.cities?.length) {
                        this.popularDomesticDestinations = provincesToLocations(
                            [{ cities: data[0].cities, provinceName: '' }]
                        );
                    }
                    this.destinationProvinces = data.slice(1);
                } else {
                    this.destinationProvinces = data;
                }
            } catch (error) {
                throw new Error('خطای غیر منتظره ای رخ داد.', { cause: error });
            } finally {
                this.isLoadingLocations = false;
            }
        };
        this.loadDestinationsJob = loader();
        await this.loadDestinationsJob;
    }

    override updatePaxCount(params: { type: PaxType; count: number }) {
        const paxCount = this.capacityInfo[params.type];
        const initPaxCount = this.initPaxCounts.find(
            pax => pax.code === params.type
        );
        if (
            !initPaxCount ||
            !paxCount ||
            params.count > paxCount.max ||
            params.count < paxCount.min
        ) {
            throw new Error(
                `حداکثر تعداد مسافران در سرویس ${this.reserveInfo.title} ${this.maxSeatCount} نفر است.`
            );
        }

        initPaxCount.count = params.count;
    }

    override popularDomesticDestinations: BusLocation[] =
        this.popularDomesticOrigins;

    originProvinces: Province[] = [];

    destinationProvinces: Province[] = [];

    override popularForeignOrigins: BusLocation[] = [
        {
            code: 'tehran',
            terminalId: 11320000,
            title: 'تهران',
        },
        {
            code: 'istanbul_turkey',
            terminalId: 117002,
            title: 'استانبول (ترکیه)',
        },
    ];

    override get domesticDestinations() {
        return provincesToLocations(
            this.destinationProvinces,
            SearchMode.Domestic
        );
    }

    override get domesticOrigins() {
        return provincesToLocations(this.originProvinces, SearchMode.Domestic);
    }

    override get foreignDestinations() {
        return provincesToLocations(
            this.destinationProvinces,
            SearchMode.Foreign
        );
    }

    override get foreignOrigins() {
        return provincesToLocations(this.originProvinces, SearchMode.Foreign);
    }

    override generateViewUrl(params: UrlStringParams<BusUrlParams>) {
        const query = this.generateViewQueries(params);
        const q = query.toString();
        const queryString = q ? `?${q}` : '';
        return {
            fullPath: `/${this.reserveType === ReserveType.Bus ? 'buses' : 'taxi'}/${
                params.origin
            }-${params.destination}${queryString}`,
            queries: Object.fromEntries(query.entries()),
        };
    }

    override getPriceLoader(
        origin: BusLocation,
        destination: BusLocation,
        tripType: TripType
    ): BusPriceLoader {
        return new BusPriceLoader({
            from: `${origin.terminalId}`,
            to: `${destination.terminalId}`,
            specificProviderId: this.specificProviderId,
            tripType,
            adultCount: this.adultCount,
            childCount: this.childCount,
            infantCount: this.infantCount,
        });
    }

    override initPaxCounts = [
        {
            code: PaxType.Adult,
            title: 'مسافر',
            subtitle: '',
            count: 1,
            min: 1,
            max: this.maxSeatCount,
        },
    ];

    override get capacityInfo(): {
        [key in PaxType]?: {
            count: number;
            min: number;
            max: number;
        };
    } {
        return {
            [PaxType.Adult]: {
                count: this.adultCount,
                min: 1,
                max: this.maxSeatCount,
            },
        };
    }

    override get reserveInfo(): ReserveInfo {
        if (this.reserveType === ReserveType.Bus) {
            return {
                type: ReserveType.Bus,
                title: 'اتوبوس',
                nameE: 'اتوبوسی',
                icon: faBus,
            };
        } else if (this.reserveType === ReserveType.Taxi) {
            return {
                type: ReserveType.Taxi,
                title: 'سواری',
                nameE: 'تاکسی و سواری',
                icon: faCar,
            };
        } else {
            throw new Error('reserve type not found!');
        }
    }

    override get viewUrl() {
        return this.generateViewUrl({
            origin: this.originCode!,
            destination: this.destinationCode!,
            departureDate: this.formattedDepartureDate,
            returnDate:
                this.tripType === TripType.RoundTrip
                    ? this.formattedReturnDate
                    : undefined,
            adultCount: this.adultCount.toString(),
        }).fullPath;
    }

    override get serviceIndexUrl() {
        return this.reserveType === ReserveType.Taxi ? '/taxi' : '/bus-ticket';
    }

    override get popularForeignDestinations() {
        return this.foreignDestinations;
    }

    override async searchLocations(
        searchKeyword: string,
        options: { target: 'origin' | 'destination'; mode: SearchMode }
    ): Promise<void> {
        await this.loadStaticLocations();
        await super.searchLocations(searchKeyword, options);
    }

    override getLocationKeywords(location: BusLocation) {
        return [
            ...super.getLocationKeywords(location),
            `${location.terminalId}`,
        ];
    }

    override get showRecentLocationsInDestinations() {
        return this.isDomestic;
    }

    async findMapRoute(busCode: string): Promise<string | null> {
        try {
            const { allRoutes } = await import('~/lib/static/contentBusMapped');
            return allRoutes.find(r => r.from === busCode)?.to ?? null;
        } catch (e) {
            reportException(e);
            return null;
        }
    }

    override async setDestinationBaseOnCode(destinationCode: string) {
        if (!destinationCode) {
            return;
        }
        await this.loadStaticLocations();
        const location = [...this.destinations, ...this.origins].find(
            o => o.code === destinationCode
        );
        this.setDestination(location || null);
    }

    override async loadLandingSearchParams(destinationCode: string) {
        const origin = destinationCode === 'tehran' ? 'esfahan' : 'tehran';
        await this.searchLocations(origin, {
            target: 'origin',
            mode: SearchMode.Domestic,
        });
        await this.searchLocations(destinationCode, {
            target: 'destination',
            mode: SearchMode.Domestic,
        });
        this.setOriginBaseOnCode(origin);
        await this.loadDestinations();
        await this.searchLocations(destinationCode, {
            target: 'destination',
            mode: SearchMode.Domestic,
        });
        this.setDestinationBaseOnCode(destinationCode);
    }

    override async loadSuggestions(params: TripShareInfo & TripShareLocations) {
        const { data } = await GetNearbyRoutesApiApiGetNearbyRoutesPost({
            getNearbyRoutesParameters: {
                from: {
                    latitude: params.origin.latitude,
                    longitude: params.origin.longitude,
                },
                to: {
                    latitude: params.destination.latitude,
                    longitude: params.destination.longitude,
                },
                departureDate: dayConfig
                    .gregory(params.departureDate)
                    .format(formatter()),
                providerId: this.specificProviderId,
            },
        });

        const route = data.routes?.[0];
        if (!route?.fromCode || !route.toCode) {
            this.suggestionResult = null;
            return;
        }
        this.suggestionResult = {
            viewUrl: this.generateViewUrl({
                origin: route.fromCode,
                destination: route.toCode,
                departureDate: dayConfig
                    .jalali(params.departureDate)
                    .format(formatter()),
                returnDate: params.returnDate
                    ? dayConfig.jalali(params.returnDate).format(formatter())
                    : undefined,
                adultCount: params.adultCount.toString(),
                childCount: params.childCount.toString(),
                infantCount: params.infantCount.toString(),
            }).fullPath,
            originTitle: route.fromName ?? '',
            destinationTitle: route.toName ?? '',
            minPrice: route.minPrice,
        };
    }
}
